import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Link, Chip, Collapse
} from '@mui/material';
import { Add, Delete, Edit, Image as ImageIcon, AttachFile, CheckCircle, Cancel, ExpandMore, ExpandLess, Lock, LockOpen } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getExpenses, getExpenseCategories, createExpense, createPerFlatExpense, updateExpense, deleteExpense, approveExpense, getMonthLockStatus, toggleMonthLock, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockExpenses, mockExpenseCategories } from '../services/mockData';

export default function Expenses() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ categoryId: '', description: '', amount: '', expenseDate: '', splitType: 'Equal' });
  const [billImage, setBillImage] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isMonthLocked, setIsMonthLocked] = useState(false);
  const fileInputRef = useRef(null);
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      setExpenses(mockExpenses);
      setCategories(mockExpenseCategories);
      return;
    }
    getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data)).catch(() => {});
    getExpenseCategories(selectedApartment).then(res => setCategories(res.data)).catch(() => {});
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
    getMonthLockStatus(selectedApartment, month, year).then(res => setIsMonthLocked(res.data.isLocked || false)).catch(() => setIsMonthLocked(false));
  }, [selectedApartment, month, year]);

  const isPerFlat = form.splitType === 'PerFlat';
  const isEqualSelected = form.splitType === 'EqualSelected';
  const selectedCount = lineItems.filter(li => li.selected).length;
  const equalSelectedAmount = (form.amount && selectedCount > 0) ? (parseFloat(form.amount) / selectedCount) : 0;

  const handleCategoryChange = (categoryId) => {
    setForm({ ...form, categoryId: categoryId });
  };

  const handleSplitTypeChange = (splitType) => {
    setForm({ ...form, splitType });
    if ((splitType === 'PerFlat' || splitType === 'EqualSelected') && apartmentUsers.length > 0) {
      // Initialize line items for all flats
      setLineItems(apartmentUsers.map(u => ({
        apartmentUserId: u.id,
        flatNumber: u.flatNumber,
        userName: u.userName,
        selected: true, // for EqualSelected - all selected by default
        amount: '',
        previousReading: '',
        meterReading: '',
        unitsConsumed: '',
        notes: ''
      })));
    } else {
      setLineItems([]);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate units consumed if both readings are provided
      if (field === 'meterReading' || field === 'previousReading') {
        const current = parseFloat(field === 'meterReading' ? value : updated[index].meterReading) || 0;
        const previous = parseFloat(field === 'previousReading' ? value : updated[index].previousReading) || 0;
        if (current > 0 && previous > 0) {
          updated[index].unitsConsumed = (current - previous).toString();
        }
      }
      return updated;
    });
  };

  const perFlatTotal = lineItems.reduce((sum, li) => sum + (parseFloat(li.amount) || 0), 0);

  const handleSubmit = async () => {
    if (!form.categoryId || !form.description || !form.expenseDate) {
      alert('Please fill all required fields (Category, Description, Date)');
      return;
    }

    if ((isPerFlat || isEqualSelected) && !editing) {
      // Per-flat or Equal among selected
      let validItems;
      if (isEqualSelected) {
        const selected = lineItems.filter(li => li.selected);
        if (selected.length === 0) { alert('Please select at least one flat'); return; }
        if (!form.amount || parseFloat(form.amount) <= 0) { alert('Please enter total amount'); return; }
        const perFlatAmount = parseFloat(form.amount) / selected.length;
        validItems = selected.map(li => ({
          apartmentUserId: li.apartmentUserId,
          flatNumber: li.flatNumber,
          amount: Math.round(perFlatAmount * 100) / 100,
          meterReading: null,
          previousReading: null,
          unitsConsumed: null,
          notes: `Equal split: ₹${parseFloat(form.amount).toFixed(2)} ÷ ${selected.length} flats`
        }));
      } else {
        validItems = lineItems.filter(li => parseFloat(li.amount) > 0).map(li => ({
          apartmentUserId: li.apartmentUserId,
          flatNumber: li.flatNumber,
          amount: parseFloat(li.amount),
          meterReading: li.meterReading ? parseFloat(li.meterReading) : null,
          previousReading: li.previousReading ? parseFloat(li.previousReading) : null,
          unitsConsumed: li.unitsConsumed ? parseFloat(li.unitsConsumed) : null,
          notes: li.notes || null
        }));
        if (validItems.length === 0) { alert('Please enter amount for at least one flat'); return; }
      }
      try {
        await createPerFlatExpense({
          apartmentId: selectedApartment,
          categoryId: parseInt(form.categoryId),
          description: form.description,
          expenseDate: form.expenseDate,
          lineItems: validItems
        });
        getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data));
      } catch (err) {
        alert(err.response?.data || 'Failed to add expense');
        return;
      }
    } else if (editing) {
      // Update
      if (!form.amount) { alert('Please enter amount'); return; }
      if (USE_MOCK_DATA) {
        const cat = categories.find(c => c.id === parseInt(form.categoryId));
        setExpenses(prev => prev.map(e => e.id === editing.id ? {
          ...e, categoryId: parseInt(form.categoryId), categoryName: cat?.name || e.categoryName,
          description: form.description, amount: parseFloat(form.amount), expenseDate: form.expenseDate
        } : e));
      } else {
        await updateExpense(editing.id, {
          categoryId: parseInt(form.categoryId), description: form.description,
          amount: parseFloat(form.amount), expenseDate: form.expenseDate,
          billImage: billImage
        });
        getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data));
      }
    } else {
      // Regular expense
      if (!form.amount || isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) {
        alert('Please enter a valid amount greater than 0');
        return;
      }
      if (USE_MOCK_DATA) {
        const cat = categories.find(c => c.id === parseInt(form.categoryId));
        setExpenses(prev => [...prev, {
          id: Date.now(), apartmentId: selectedApartment, apartmentName: 'Sunshine Residency',
          categoryId: parseInt(form.categoryId), categoryName: cat?.name || '',
          description: form.description, amount: parseFloat(form.amount),
          expenseDate: form.expenseDate, billImageUrl: billImage ? URL.createObjectURL(billImage) : null,
          createdByUser: 'Debug Admin', createdAt: new Date().toISOString()
        }]);
      } else {
        try {
          await createExpense({
            apartmentId: selectedApartment, categoryId: parseInt(form.categoryId),
            description: form.description, amount: parseFloat(form.amount),
            expenseDate: form.expenseDate, billImage: billImage
          });
          getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data));
        } catch (err) {
          alert(err.response?.data || 'Failed to add expense. Please check all fields.');
          return;
        }
      }
    }
    handleDialogClose();
  };

  const handleEdit = (exp) => {
    setEditing(exp);
    setForm({ categoryId: exp.categoryId, description: exp.description, amount: exp.amount.toString(), expenseDate: exp.expenseDate.split('T')[0], splitType: exp.splitType || 'Equal' });
    setBillImage(null);
    setLineItems([]);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this expense?')) {
      if (USE_MOCK_DATA) { setExpenses(prev => prev.filter(e => e.id !== id)); }
      else { await deleteExpense(id); getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data)); }
    }
  };

  const handleApprove = async (id, approved) => {
    let rejectionReason = null;
    if (!approved) { rejectionReason = prompt('Reason for rejection (optional):'); }
    if (USE_MOCK_DATA) { setExpenses(prev => prev.map(e => e.id === id ? { ...e, approvalStatus: approved ? 'Approved' : 'Rejected', rejectionReason } : e)); }
    else { await approveExpense(id, approved, rejectionReason); getExpenses(selectedApartment, month, year).then(res => setExpenses(res.data)); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { alert('File size must be less than 2MB'); return; }
      setBillImage(file);
    }
  };

  const handleDialogClose = () => {
    setOpen(false); setEditing(null);
    setForm({ categoryId: '', description: '', amount: '', expenseDate: '', splitType: 'Equal' });
    setBillImage(null); setLineItems([]);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Expenses</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          {isAdmin() && !isMonthLocked && (
            <Button variant="contained" startIcon={<Add />} fullWidth onClick={() => { setEditing(null); setForm({ categoryId: '', description: '', amount: '', expenseDate: '', splitType: 'Equal' }); setLineItems([]); setOpen(true); }}>Add Expense</Button>
          )}
        </Grid>
      </Grid>

      {/* Month Lock Status & Toggle */}
      {isAdmin() && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMonthLocked ? (
            <>
              <Chip icon={<Lock />} label="Month Locked" color="error" size="small" />
              <Button size="small" variant="outlined" startIcon={<LockOpen />}
                onClick={async () => { await toggleMonthLock(selectedApartment, month, year, false); setIsMonthLocked(false); }}>
                Unlock
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined" color="warning" startIcon={<Lock />}
              onClick={async () => { if (window.confirm('Lock this month? No one can add/edit/delete expenses after locking.')) { await toggleMonthLock(selectedApartment, month, year, true); setIsMonthLocked(true); } }}>
              Lock Month
            </Button>
          )}
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Bill</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created By</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map(exp => (
              <React.Fragment key={exp.id}>
                <TableRow sx={{ opacity: exp.approvalStatus === 'Rejected' ? 0.6 : 1 }}>
                  <TableCell>
                    {exp.lineItems && exp.lineItems.length > 0 && (
                      <IconButton size="small" onClick={() => setExpandedRow(expandedRow === exp.id ? null : exp.id)}>
                        {expandedRow === exp.id ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{new Date(exp.expenseDate).toLocaleDateString()}</TableCell>
                  <TableCell>{exp.categoryName}</TableCell>
                  <TableCell>
                    {exp.description}
                    {exp.splitType === 'PerFlat' && (
                      <Chip label="Per Flat" size="small" sx={{ ml: 1 }} color="info" variant="outlined" />
                    )}
                    {exp.lineItems && exp.lineItems.length > 0 && (
                      <Chip label={`${exp.lineItems.length} flats`} size="small" sx={{ ml: 0.5 }} color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">₹{exp.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    {exp.billImageUrl ? (
                      <Link href={exp.billImageUrl} target="_blank" rel="noopener">
                        <Chip icon={<ImageIcon />} label="View" size="small" clickable color="primary" variant="outlined" />
                      </Link>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={exp.approvalStatus || 'Pending'} size="small"
                      color={exp.approvalStatus === 'Approved' ? 'success' : exp.approvalStatus === 'Rejected' ? 'error' : 'warning'} />
                    {exp.rejectionReason && <Typography variant="caption" display="block" color="error">{exp.rejectionReason}</Typography>}
                  </TableCell>
                  <TableCell>{exp.createdByUser}</TableCell>
                  {isAdmin() && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {!isMonthLocked ? (
                        <>
                          {(exp.approvalStatus === 'Pending' || !exp.approvalStatus) && (
                            <>
                              <IconButton onClick={() => handleApprove(exp.id, true)} color="success" title="Approve"><CheckCircle /></IconButton>
                              <IconButton onClick={() => handleApprove(exp.id, false)} color="error" title="Reject"><Cancel /></IconButton>
                            </>
                          )}
                          <IconButton onClick={() => handleEdit(exp)} color="primary"><Edit /></IconButton>
                          <IconButton onClick={() => handleDelete(exp.id)} color="error"><Delete /></IconButton>
                        </>
                      ) : (
                        <Chip icon={<Lock />} label="Locked" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {/* Expandable line items row */}
                {exp.lineItems && exp.lineItems.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={isAdmin() ? 9 : 8} sx={{ p: 0 }}>
                      <Collapse in={expandedRow === exp.id}>
                        <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#f5f5f5', overflowX: 'auto' }}>
                          <Typography variant="subtitle2" gutterBottom>Per-Flat Breakdown</Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Flat</TableCell>
                                <TableCell>Resident</TableCell>
                                <TableCell align="right">Prev Reading</TableCell>
                                <TableCell align="right">Current Reading</TableCell>
                                <TableCell align="right">Units</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Notes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {exp.lineItems.map(li => (
                                <TableRow key={li.id}>
                                  <TableCell>{li.flatNumber}</TableCell>
                                  <TableCell>{li.userName}</TableCell>
                                  <TableCell align="right">{li.previousReading ?? '—'}</TableCell>
                                  <TableCell align="right">{li.meterReading ?? '—'}</TableCell>
                                  <TableCell align="right">{li.unitsConsumed ?? '—'}</TableCell>
                                  <TableCell align="right">₹{li.amount.toFixed(2)}</TableCell>
                                  <TableCell>{li.notes || '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {expenses.length > 0 && (
              <TableRow>
                <TableCell /><TableCell colSpan={3}><strong>Approved Total</strong></TableCell>
                <TableCell align="right"><strong>₹{expenses.filter(e => e.approvalStatus === 'Approved').reduce((sum, e) => sum + e.amount, 0).toFixed(2)}</strong></TableCell>
                <TableCell colSpan={4} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={open} onClose={handleDialogClose} maxWidth={isPerFlat && !editing ? 'md' : 'sm'} fullWidth>
        <DialogTitle>{editing ? 'Edit Expense' : isPerFlat ? 'Add Per-Flat Expense' : 'Add Expense'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select value={form.categoryId} label="Category" onChange={(e) => handleCategoryChange(e.target.value)}>
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ''}{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Description" margin="normal" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

          {/* Split Type selector - only for new expenses */}
          {!editing && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Split Type</InputLabel>
              <Select value={form.splitType} label="Split Type" onChange={(e) => handleSplitTypeChange(e.target.value)}>
                <MenuItem value="Equal">Equal Share (split equally among all flats)</MenuItem>
                <MenuItem value="EqualSelected">Equal Among Selected Flats (choose which flats)</MenuItem>
                <MenuItem value="PerFlat">Per Flat (meter reading / individual amount)</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Show amount field for Equal, EqualSelected, or editing */}
          {(!isPerFlat || editing) && (
            <TextField fullWidth label="Amount" type="number" margin="normal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          )}

          <TextField fullWidth label="Date" type="date" margin="normal" InputLabelProps={{ shrink: true }} value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} />

          {/* Equal Among Selected - flat selection checkboxes */}
          {isEqualSelected && !editing && lineItems.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Select Flats — {selectedCount} selected × ₹{equalSelectedAmount.toFixed(2)} each = ₹{(parseFloat(form.amount) || 0).toFixed(2)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {lineItems.map((li, idx) => (
                  <Box key={li.apartmentUserId}
                    onClick={() => setLineItems(prev => {
                      const updated = [...prev];
                      updated[idx] = { ...updated[idx], selected: !updated[idx].selected };
                      return updated;
                    })}
                    sx={{
                      p: 1, borderRadius: 1, cursor: 'pointer', border: '1px solid',
                      borderColor: li.selected ? 'primary.main' : '#e0e0e0',
                      bgcolor: li.selected ? '#e3f2fd' : '#fff',
                      minWidth: '80px', textAlign: 'center'
                    }}>
                    <Typography variant="body2" fontWeight={li.selected ? 700 : 400}>{li.flatNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">{li.userName}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Per-flat line items */}
          {isPerFlat && !editing && lineItems.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Per-Flat Breakdown — Total: ₹{perFlatTotal.toFixed(2)}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Flat</TableCell>
                      <TableCell>Resident</TableCell>
                      <TableCell>Prev Reading</TableCell>
                      <TableCell>Current Reading</TableCell>
                      <TableCell>Units</TableCell>
                      <TableCell>Amount (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((li, idx) => (
                      <TableRow key={li.apartmentUserId}>
                        <TableCell>{li.flatNumber}</TableCell>
                        <TableCell>{li.userName}</TableCell>
                        <TableCell>
                          <TextField size="small" type="number" variant="standard"
                            value={li.previousReading} onChange={(e) => handleLineItemChange(idx, 'previousReading', e.target.value)}
                            sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" variant="standard"
                            value={li.meterReading} onChange={(e) => handleLineItemChange(idx, 'meterReading', e.target.value)}
                            sx={{ width: 80 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" variant="standard" disabled
                            value={li.unitsConsumed} sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number" variant="standard"
                            value={li.amount} onChange={(e) => handleLineItemChange(idx, 'amount', e.target.value)}
                            sx={{ width: 90 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Bill Image Upload - for new and editing expenses */}
          {!isPerFlat && (
            <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".jpg,.jpeg,.png,.pdf,.webp" style={{ display: 'none' }} />
              <Button variant="outlined" startIcon={<AttachFile />} onClick={() => fileInputRef.current?.click()}>
                {billImage ? 'Change Bill Image' : 'Upload Bill Image'}
              </Button>
              {billImage && (
                <Box sx={{ mt: 1 }}>
                  <Chip label={billImage.name} onDelete={() => setBillImage(null)} size="small" color="success" />
                  <Typography variant="caption" display="block" color="text.secondary">{(billImage.size / 1024).toFixed(1)} KB</Typography>
                </Box>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>Accepted: JPG, PNG, PDF, WebP (max 2MB)</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : isPerFlat ? `Add (₹${perFlatTotal.toFixed(2)})` : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
